import cookie from 'react-cookie';
import jwtDecode from 'jwt-decode';

import {
  b64DecodeUnicode,
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
  uuid,
} from '../../../utils';

import {
  AUTH_COOKIE_NAME,
  LOCK_SECRET_NAME,
  USER_COOKIE_NAME,
  USER_DISPLAY_NAME_COOKIE_NAME,
  FAR_FUTURE_DATE,
} from './constants';

const authService = {};

/**
 * Check secret in auth result matches local storage
 * Return success and return URL subject to verification
 */
authService.parseAuthResult = (authResult) => {
  const storedSecret = getLocalStorage(LOCK_SECRET_NAME);
  removeLocalStorage(LOCK_SECRET_NAME);
  try {
    const { secret, returnUrl } = JSON.parse(
      b64DecodeUnicode(authResult.state),
    );
    if (secret === storedSecret) {
      return {
        success: true,
        returnUrl: authService.verifyReturnUrl(returnUrl),
      };
    }
  } catch (e) {
    // Parse failed
  }
  return {
    success: false,
  };
};

/**
 * Verify return URL is not an absolute URL, e.g. safe for router and not external
 */
authService.verifyReturnUrl = (returnUrl) => {
  if (new RegExp('^(?:[a-z]+:)?//', 'i').test(returnUrl) || !returnUrl) {
    // URL contains protocol, e.g. http://
    return '/';
  }
  return returnUrl;
};

/**
 * Create a uuid-like secret and save to local storage
 */
authService.setSecret = () => {
  const secret = uuid();
  setLocalStorage(LOCK_SECRET_NAME, secret);
  return secret;
};

/**
 * Save the user token to cookie, maintaining token's expiry timestamp
 */
authService.setAuthCookie = (idToken, exp) => {
  cookie.save(AUTH_COOKIE_NAME, idToken, {
    domain: window.location.hostname,
    expires: new Date(exp * 1000),
    path: '/', // Required to be used site-wide
    secure: !__DEVELOPMENT__,
  });
};

/**
 * Save the user ID to cookie, without expiry
 */
authService.setUserCookie = (primaryIdentityId) => {
  cookie.save(USER_COOKIE_NAME, primaryIdentityId, {
    domain: window.location.hostname,
    expires: new Date(FAR_FUTURE_DATE),
    path: '/', // Required to be used site-wide
    secure: !__DEVELOPMENT__,
  });
};

/**
 * Save the user display name to cookie, without expiry
 */
authService.setUserDisplayNameCookie = (displayName) => {
  cookie.save(USER_DISPLAY_NAME_COOKIE_NAME, displayName, {
    domain: window.location.hostname,
    expires: new Date(FAR_FUTURE_DATE),
    path: '/', // Required to be used site-wide
    secure: !__DEVELOPMENT__,
  });
};
/**
 * Get the user display name from the cookies
 */
authService.getUserDisplayName = () =>
  cookie.load(USER_DISPLAY_NAME_COOKIE_NAME);

/**
 * Get the auth token from the cookie
 */
authService.getAuthCookie = () => {
  return cookie.load(AUTH_COOKIE_NAME);
};
authService.getIdToken = () => {
  return cookie.load(AUTH_COOKIE_NAME);
};

/**
 * Get the user ID from the cookie
 */
authService.getUserCookie = () => cookie.load(USER_COOKIE_NAME);
authService.getPrimaryIdentityId = () => cookie.load(USER_COOKIE_NAME);
/**
 * Checks to see if user has the cookie. Returns boolean.
 */
authService.hasUserCookie = () => !!authService.getUserCookie();

/**
 * Remove the auth cookie entirely
 */
authService.removeAuthCookie = () => {
  cookie.remove(AUTH_COOKIE_NAME, {
    // Matching params are required to remove the cookie
    domain: window.location.hostname,
    path: '/',
  });
};

/**
 * Remove the user cookie entirely
 */
authService.removeUserCookie = () => {
  cookie.remove(USER_COOKIE_NAME, {
    // Matching params are required to remove the cookie
    domain: window.location.hostname,
    path: '/',
  });
};

/**
 * Remove the user display name cookie entirely
 */
authService.removeUserDisplayNameCookie = () => {
  cookie.remove(USER_DISPLAY_NAME_COOKIE_NAME, {
    // Matching params are required to remove the cookie
    domain: window.location.hostname,
    path: '/',
  });
};

authService.unknownUserIsNotPreviousLoggedInUser = () => {
  authService.removeUserCookie();
  authService.removeUserDisplayNameCookie();
};

/**
 * Authenticate the app, with an result object from Auth0
 */
authService.login = (authResult) => {
  authService.setAuthCookie(authResult.idToken, authResult.idTokenPayload.exp);
};

/**
 * Unauthenticate the app, by removing the auth cookie (only) and bouncing to Auth0
 */
authService.logout = (isManual) => {
  authService.removeAuthCookie();
  if (isManual) {
    authService.removeUserCookie();
    authService.removeUserDisplayNameCookie();
  }
  if (__CLIENT__) {
    window.location.href = `https://${AUTH0_CLIENT_DOMAIN}/v2/logout?returnTo=${encodeURIComponent(
      WEB_ROOT,
    )}&client_id=${AUTH0_CLIENT_ID}`;
  }
};

/**
 * Checks if there is a cookie and it's still valid
 */
authService.loggedIn = () => !!authService.getAuthCookie();

authService.getUserFromJWT = () => {
  try {
    // Cookie is required
    const authCookie = authService.getAuthCookie();
    // JWT is well-formed
    return jwtDecode(authCookie);
  } catch (error) {
    return false;
    // JWT could not be decoded, or cookie doesn't exist
  }
};
/**
 * Handler called when entering authenticated routes from Router
 * Checked for a well-formed JWT in the auth token, does not verify it
 * Verification takes place on server request, if this fails the cookie cleared
 */
authService.authenticateRoute = (props, replace) => {
  const {
    location: { pathname, hash, search },
  } = props;
  let user;
  try {
    // Cookie is required
    const authCookie = authService.getAuthCookie();
    // JWT is well-formed
    user = jwtDecode(authCookie);
  } catch (error) {
    // JWT could not be decoded, or cookie doesn't exist
  }
  if (!user) {
    replace({
      pathname: '/login',
      query: { returnUrl: `${pathname}${search}${hash}` },
    });
  }
};

/**
 * Handler called when entering admin routes from Router
 * Decodes the auth token and checks that the user roles contain 'admin'.
 * Redirects to logout if the user is not an admin.
 */
authService.adminRoute = (_, replace) => {
  let admin = false;
  try {
    // Cookie is required
    const authCookie = authService.getAuthCookie();
    const user = jwtDecode(authCookie);
    // User roles includes admin
    if (user && user.roles.includes('admin')) {
      admin = true;
    }
  } catch (error) {
    // JWT could not be decoded, or cookie doesn't exist
  }
  if (!admin) {
    // TODO: This should probably 404 with no sign that there is an admin route here
    // To logout the user is intentionally annoying, but may also be annoying for dev/testing.
    replace({
      pathname: '/logout',
    });
  }
};
export const initAuth = async () => {
  return authService;
};
export {
  AUTH_COOKIE_NAME,
  LOCK_SECRET_NAME,
  REFERRER_COOKIE_NAME,
  USER_COOKIE_NAME,
  USER_DISPLAY_NAME_COOKIE_NAME,
  FAR_FUTURE_DATE,
} from './constants';

export default authService;
